import { ETokenBudgetScope, ETokenBudgetUnit } from "@frontend-shared/types";
import {
  buildUsageThresholdMetaLine,
  formatUsageLimitAmount,
  resolveUsageThresholdBody,
  resolveUsageThresholdPercent,
} from "@frontend-shared/utils";
import { formatDistanceToNow } from "date-fns";
import { AlertTriangle, Ban } from "lucide-react";
import { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { NotificationCardShell } from "../NotificationCardShell";
import { resolveDateFnsLocale } from "../utils/resolve-date-fns-locale";
import { resolveUsageThresholdSeverity } from "../utils/resolve-usage-threshold-severity";
import type { IUsageThresholdReachedCardProps } from "./types";
import { UsageThresholdMeter } from "./UsageThresholdMeter";

const UsageThresholdReachedCardComponent = ({
  uuid,
  data,
  createdAt,
  isRead,
  isTokenUsageEnabled,
  onViewUsageLimits,
  onViewUsage,
}: IUsageThresholdReachedCardProps): React.JSX.Element => {
  const { t, i18n } = useTranslation("notifications");

  const isOwnerScope =
    data.scope === ETokenBudgetScope.ORGANIZATION ||
    data.scope === ETokenBudgetScope.PROJECT;
  const isPersonalScope = data.scope === ETokenBudgetScope.PERSONAL;
  const hasOwnerDestination = isOwnerScope && Boolean(data.organizationUuid);
  // NOTE: USER (in-org) alerts get no CTA — members have no page showing
  // in-org usage. The personal CTA also needs the token-usage tab flag on,
  // or its deep-link would bounce to the default profile tab.
  const hasCta =
    (isPersonalScope && isTokenUsageEnabled) || hasOwnerDestination;

  // NOTE: NSAI-5612 — the two figures below are in this denomination. Absent
  // on alerts stored before cost caps shipped, and every one of those is a
  // token cap.
  const unit = data.unit ?? ETokenBudgetUnit.TOKENS;

  const severity = useMemo(
    () => resolveUsageThresholdSeverity(data.threshold),
    [data.threshold],
  );

  const percent = useMemo(
    () =>
      resolveUsageThresholdPercent({
        usedTokens: data.usedTokens,
        limitTokens: data.limitTokens,
        threshold: data.threshold,
      }),
    [data.limitTokens, data.usedTokens, data.threshold],
  );

  // NOTE: The distance phrase sits inside a translated sentence, so it has to
  // follow the app language rather than date-fns' English default.
  const metaLine = useMemo(
    () =>
      buildUsageThresholdMetaLine({
        usedTokens: data.usedTokens,
        limitTokens: data.limitTokens,
        unit,
        resetAt: data.resetAt,
        translate: t,
        formatResetDistance: (resetDate) =>
          formatDistanceToNow(resetDate, {
            addSuffix: true,
            locale: resolveDateFnsLocale(i18n.language),
          }),
      }),
    [t, i18n.language, data.usedTokens, data.limitTokens, unit, data.resetAt],
  );

  // NOTE: Exact figures on hover — the meta line shows compact "8K" counts, so
  // the title exposes the precise numbers for anyone who needs them, in
  // whichever denomination the cap is set in.
  const exactUsageTitle = useMemo(
    () =>
      `${formatUsageLimitAmount({
        amount: data.usedTokens,
        unit,
      })} / ${formatUsageLimitAmount({ amount: data.limitTokens, unit })}`,
    [data.usedTokens, data.limitTokens, unit],
  );

  const body = useMemo(
    () => resolveUsageThresholdBody({ data, translate: t }),
    [data, t],
  );

  const cardBody = useMemo(
    () => (
      <div className="space-y-2">
        <p>{body}</p>
        <UsageThresholdMeter
          percent={percent}
          ariaLabel={t("type.usage_threshold_reached.meter_aria", { percent })}
          fillClassName={severity.meterFillClassName}
        />
        <p
          className="text-xs text-muted-foreground [overflow-wrap:anywhere]"
          title={exactUsageTitle}
        >
          {metaLine}
        </p>
      </div>
    ),
    [body, percent, t, severity.meterFillClassName, metaLine, exactUsageTitle],
  );

  const handleCtaClick = useCallback((): void => {
    if (isOwnerScope) {
      if (data.organizationUuid) {
        onViewUsageLimits(data.organizationUuid);
      }
      return;
    }
    onViewUsage();
  }, [isOwnerScope, data.organizationUuid, onViewUsageLimits, onViewUsage]);

  return (
    <NotificationCardShell
      uuid={uuid}
      variantTestId="usage_threshold_reached"
      createdAt={createdAt}
      isRead={isRead}
      icon={
        severity.isLimitReached ? (
          <Ban className="h-5 w-5" />
        ) : (
          <AlertTriangle className="h-5 w-5" />
        )
      }
      iconBgClassName={severity.iconWrapperClassName}
      {...(hasCta
        ? {
            ctaLabel: isOwnerScope ? t("view_usage_limits") : t("view_usage"),
            onCtaClick: handleCtaClick,
          }
        : {})}
      body={cardBody}
    />
  );
};

export const UsageThresholdReachedCard = memo(
  UsageThresholdReachedCardComponent,
);
